/**
 * selectSectionWorkflow
 *
 * Derives `{sectionId, triggers, variables, items, itemBoxIds}` from the
 * Redux state's `undoGroup.present` slice. This is the single source of truth
 * for "what does the InteractivityStudio Workflow tab need from the store?"
 * and is reused by the WorkflowTab container (Phase 1.2), the ActionNode
 * editor (Phase 5), and the VariablesRail (Phase 6).
 *
 * Memoization: hand-rolled single-cache memoizer keyed on the three inputs
 * that actually affect the output — `(navItemsById, pluginToolbarsById,
 * navItemSelected)`. Changes to unrelated slices of state do not invalidate
 * the cache. `reselect` is not a project dependency, so we don't pull it in
 * for a single selector.
 */

const EMPTY = Object.freeze({
  sectionId: null,
  triggers: [],
  variables: [],
  items: [],
  itemBoxIds: new Set(),
  unreachableNodeIds: [],
  scoringMode: "none",
});

let lastNavItemsById;
let lastPluginToolbarsById;
let lastNavItemSelected;
let lastResult = EMPTY;
let hasCached = false;

// A section id may be `0` (course root) — strictly null/undefined means missing.
const hasId = (v) => v !== null && v !== undefined && v !== "";

function compute(navItemsById, pluginToolbarsById, navItemSelected) {
  const selected = hasId(navItemSelected) ? navItemsById[navItemSelected] : null;
  // If the selected nav item is itself a section, use IT as the sectionId.
  // Otherwise walk up to its parent. This keeps "section selected" and "any
  // scene under that section selected" both resolving to the same workflow
  // bucket — without this, authoring while a section is selected writes the
  // trigger to the section's PARENT (the course root), and on reload the
  // selector — now looking at a scene whose `.parent` is the section — finds
  // an empty workflow even though the data is on disk under the root.
  // Section detection: the upstream data uses ID prefix `se-` for sections,
  // but tests build minimal fixtures with `type: "section"` — accept either.
  const isSection =
    selected &&
    (selected.type === "section" ||
      (typeof selected.id === "string" && selected.id.indexOf("se-") !== -1));
  // scene.parent may legitimately be `0` (course root) — treat as a valid section.
  const sectionId = isSection
    ? selected.id
    : selected && hasId(selected.parent)
      ? selected.parent
      : null;
  const section = hasId(sectionId) ? navItemsById[sectionId] : null;
  const workflow = (section && section.workflow) || {};

  const items = [];
  const itemBoxIds = new Set();
  if (hasId(sectionId)) {
    Object.keys(navItemsById).forEach((id) => {
      const item = navItemsById[id];
      // Match `0` as parent too — JS object keys are strings, the parent value
      // can be a number; use == so 0 == "0" matches and so does a string id.
      // eslint-disable-next-line eqeqeq
      if (!item || item.parent != sectionId || !Array.isArray(item.boxes)) return;
      item.boxes.forEach((boxId) => {
        if (itemBoxIds.has(boxId)) return;
        itemBoxIds.add(boxId);
        const toolbar = pluginToolbarsById[boxId];
        const toolbarItems =
          toolbar && toolbar.state && Array.isArray(toolbar.state.items) ? toolbar.state.items : [];
        toolbarItems.forEach((it) => items.push(it));
      });
    });
  }

  const triggers = Array.isArray(workflow.triggers) ? workflow.triggers : [];
  const rawMode = workflow.scoringMode;
  const scoringMode = rawMode === "static" || rawMode === "dynamic" ? rawMode : "none";
  return {
    sectionId,
    triggers,
    variables: Array.isArray(workflow.variables) ? workflow.variables : [],
    items,
    itemBoxIds,
    unreachableNodeIds: computeUnreachableNodeIds(items, triggers),
    scoringMode,
  };
}

// Items / triggers / end-screens with no incoming edges (other than the
// implicit start->item dashed entry) are surfaced as a warning in the toolbar
// — typical cause is an EndScreen or BranchingQuestion alternative that the
// author hasn't wired up yet, or a trigger orphaned from its plugin box.
//
// The "implicit start" exclusion: every plain item gets a dashed start->item
// edge from buildGraph so it is technically reachable. We don't count that for
// the warning because it's not a real authored connection — it's a layout
// anchor. An item is "unreachable" if no OTHER node points at it (no
// BranchingQuestion alt edge, no replayItem edge, no jumpScene-to-it... which
// today doesn't exist for items but might for EndScreens once scenes can host
// them). For triggers we exclude the implicit start->trigger dashed orphan
// anchor on the same grounds.
function computeUnreachableNodeIds(items, triggers) {
  const safeItems = Array.isArray(items) ? items : [];
  const safeTriggers = Array.isArray(triggers) ? triggers : [];

  // Collect every "real" incoming edge target. Implicit start->* dashed edges
  // are excluded — those are layout-only anchors, not authored wiring.
  const incoming = new Set();

  // BranchingQuestion alternatives: each alt with targetSceneId points at a
  // sceneRef node (not in our node set since we're scoped to items/triggers),
  // and each alt with targetActionChainId points at action:<chainId>:0 which
  // belongs to a trigger whose chain root the alt connects to. The action
  // node itself isn't in the in/out set we care about (it inherits reach via
  // its triggerId). What we DO record: the trigger whose id matches the
  // chainId becomes reachable from the BQ.
  safeItems.forEach((item) => {
    if (!item || item.kind !== "BranchingQuestion") return;
    const params = (item && item.params) || {};
    const alternatives = Array.isArray(params.alternatives) ? params.alternatives : [];
    alternatives.forEach((alt) => {
      if (!alt) return;
      // targetActionChainId references a trigger id — that trigger gains
      // an incoming edge.
      if (alt.targetActionChainId) {
        incoming.add(`trigger:${alt.targetActionChainId}`);
      }
    });
  });

  // Triggers with a real pluginBoxId pointing at an existing item create the
  // item->trigger edge — so the trigger is reachable via its owning item.
  // Conversely, the item is reachable from a replayItem action targeting it.
  safeTriggers.forEach((trigger) => {
    if (!trigger) return;
    if (trigger.pluginBoxId && trigger.pluginBoxId !== "*") {
      // The trigger has an item->trigger edge — trigger is reachable.
      if (safeItems.some((it) => it && it.id === trigger.pluginBoxId)) {
        incoming.add(`trigger:${trigger.id}`);
      }
    }
    const actions = Array.isArray(trigger.actions) ? trigger.actions : [];
    actions.forEach((action) => {
      if (!action) return;
      if (action.kind === "replayItem" && action.pluginBoxId) {
        incoming.add(`item:${action.pluginBoxId}`);
      }
      // jumpScene targets a sceneRef, not a node in our items/triggers set —
      // an EndScreen isn't a scene, so this doesn't make EndScreens reachable.
    });
  });

  // Walk every item / trigger and flag the ones without an incoming "real"
  // edge. EndScreens are held to a stricter standard: they MUST be the target
  // of an authored edge (a BranchingQuestion alternative, a future jumpItem,
  // etc.) — they are not reachable via the implicit start->item dashed anchor
  // because reaching an end-screen as the first thing makes no pedagogical
  // sense. Plain items, by contrast, count the implicit start anchor as a
  // legitimate entry (most authoring sessions have one such item per scene).
  const unreachable = [];
  safeItems.forEach((item) => {
    if (!item || !item.id) return;
    const isEndScreen = item.kind === "EndScreen";
    const hasIncoming = incoming.has(`item:${item.id}`);
    if (isEndScreen && !hasIncoming) {
      unreachable.push(`item:${item.id}`);
    }
  });
  safeTriggers.forEach((trigger) => {
    if (!trigger || !trigger.id) return;
    // Triggers without a real owning item OR a BQ alt pointing at them are
    // floating orphans — they only render because buildGraph anchors them to
    // start with a dashed edge. That is exactly the case the warning is for.
    if (!incoming.has(`trigger:${trigger.id}`)) {
      unreachable.push(`trigger:${trigger.id}`);
    }
  });
  return unreachable;
}

export const selectSectionWorkflow = (state) => {
  const present = (state && state.undoGroup && state.undoGroup.present) || {};
  const navItemsById = present.navItemsById || {};
  const pluginToolbarsById = present.pluginToolbarsById || {};
  const navItemSelected = present.navItemSelected;

  if (
    hasCached &&
    navItemsById === lastNavItemsById &&
    pluginToolbarsById === lastPluginToolbarsById &&
    navItemSelected === lastNavItemSelected
  ) {
    return lastResult;
  }

  lastNavItemsById = navItemsById;
  lastPluginToolbarsById = pluginToolbarsById;
  lastNavItemSelected = navItemSelected;
  lastResult = compute(navItemsById, pluginToolbarsById, navItemSelected);
  hasCached = true;
  return lastResult;
};

// Exposed for tests that need to reset the cache between cases. Internal only.
export const __resetSelectSectionWorkflowCache = () => {
  lastNavItemsById = undefined;
  lastPluginToolbarsById = undefined;
  lastNavItemSelected = undefined;
  lastResult = EMPTY;
  hasCached = false;
};
